<script setup lang="ts">
import { useJoinStore, useNationStore } from "@/store";
import { useFormBuilder } from "@/services";
import { localize } from "@/utils";
import { vMaska } from "maska";
import { computed, onMounted, reactive, ref, watch } from "vue";
import router from "@/router";
import { postPlayersSignUp } from "@/api/signup/postPlayersSignUp";

const { member } = useJoinStore();
const isKorean = member.country.code == "KR";


const { getPhone } = useNationStore();
const phoneCode = computed(() => getPhone(member.country.code));

const { validate } = useFormBuilder();

const form = reactive({
  phone: "",
  company: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
});

const errors = ref<Record<string, boolean>>({});
const submitted = ref(false);

onMounted(init);

/**
 * 초기화
 */
function init() {
  validate();
}

const validateForm = () => {
  const newErrors: Record<string, boolean> = {};
  if (!form.phone.trim()) {
    newErrors.phone = true;
  }

  if (!form.company.trim()) {
    newErrors.company = true;
  }

  if (!form.postalCode.trim()) {
    newErrors.postalCode = true;
  }

  if (!form.address1.trim()) {
    newErrors.address1 = true;
  }

  if (!form.address2.trim()) {
    newErrors.address2 = true;
  }

  // 해외만 필수
  if (!isKorean) {
    if (!form.city.trim()) {
      newErrors.city = true;
    }

    if (!form.state.trim()) {
      newErrors.state = true;
    }
  }
  errors.value = newErrors;
  return Object.keys(newErrors).length === 0;
};

const done = async() => {
  submitted.value = true;
  if (!validateForm()) {
    return;
  }
  try {
    const res = await postPlayersSignUp({
      verificationRequestId: member.verificationRequestId,
      loginId: member.loginId,
      password: member.password,
      firstName: member.name.firstName,
      lastName: member.name.lastName,
      birthDate: member.birthDate,
      gender: member.sex,
      countryCode: member.country.code,
      contactCountryNumber: member.phoneNumber.countryCode,
      contactNumber: member.phoneNumber.number,
      agreementId: member.agreement.id,
      agreementVersion: member.agreement.version,
      clinicName: form.company,
      clinicContactNumber: form.phone,
      clinicAddress: {
        postalCode: form.postalCode,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.state,
      },
    });
    if(res.isSuccess) {
      router.push("/home/join/complete/players");
    } else {
      if(res.error.code === "E_VALIDATION") {
        alert(localize("입력값이 올바르지 않습니다."));
        return;
      }
      if(res.error.code === "E_MEMBEERSHIP_001") {
        alert(localize("이미 존재하는 로그인 ID입니다."));
        return;
      }
      if(res.error.code === "E_VERIFICATION_001") {
        alert(localize("인증 요청을 찾을 수 없습니다."));
        return; 
      }
      if(res.error.code === "E_BODY_001"){
        alert(localize("요청 본문이 누락되었거나 형식이 올바르지 않습니다."))
      }
    }
  } catch (err) {
    alert(localize('서버 연결에 실패하였습니다.<br/>잠시후, 다시 시도해 주세요.'));
  }
}

watch(
  form,
  () => submitted.value && validateForm(),
  { deep: true }
);
</script>

<template>
  <div class="join-box">
    <div class="progress-line">
      <div class="line" style="width: 80%"></div>
    </div>
    <div class="step-wrap">
      <b>STEP 4</b>
      <h2>{{ localize("클리닉 정보 입력") }}</h2>
    </div>
    <div class="email">{{ member.loginId }}</div>
    <form @submit.prevent="done" class="join-form">
      <div class="form-group">
        <label class="form-label" for="tel">{{ localize("클리닉 번호") }}
          <span class="required">*</span>
        </label>
        <div class="input-group">
          <p class="country-code">+{{ phoneCode }}</p>
          <input
            type="tel"
            id="tel"
            class="form-control curved"
            v-model="form.phone"
            v-maska
            data-maska="#"
            data-maska-tokens="#:[0-9]:repeated"
            :placeholder="localize('숫자만 입력')"
            :class="{ error: errors.phone }"
          />
        </div>
      </div>

      <!-- 대한민국 전용-->
      <span v-if="isKorean">
        <div class="form-group">
          <label class="form-label" for="office-name">{{ localize("오피스 이름") }}
            <span class="required">*</span>
          </label>
          <div class="input-group">
            <input v-model="form.company" type="text" id="office-name" class="form-control curved"
            :placeholder="localize('오피스 이름')"
            :class="{ error: errors.company }"/>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label" for="office-address">{{ localize("오피스 주소") }}
            <span class="required">*</span>
          </label>
          <div>
            <label for="postalCode" class="detail mb-1">{{ localize('우편번호') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.postalCode"
                type="text"
                id="postalCode"
                class="form-control curved"
                :placeholder="localize('우편번호')"
                :class="{ error: errors.postalCode }"
              />
            </div>
          </div>
          <div class="mb-4">
            <label for="address1" class="detail mb-1">{{ localize('주소') }}
              <span class="required">*</span>
            </label>
            <div class="input-group mt-1">
              <input
                v-model="form.address1"
                type="text"
                id="address1"
                class="form-control curved"
                :placeholder="localize('주소 입력')"
                :class="{ error: errors.address1 }"
              />
            </div>
            <div class="input-group mt-2">
              <input
                v-model="form.address2"
                type="text"
                id="address2"
                class="form-control curved"
                :placeholder="localize('상세 주소 입력')"
                :class="{ error: errors.address2 }"
              />
            </div>
          </div>
        </div>
      </span>
      <!-- //대한민국 전용-->

      <!-- 해외 전용-->
      <span v-else>
        <div class="form-group">
          <label class="form-label" for="office-name">{{ localize("오피스 이름") }}
            <span class="required">*</span>
          </label>
          <div class="input-group">
            <input v-model="form.company" type="text" id="office-name" class="form-control curved" 
            :placeholder="localize('오피스 이름')"
            :class="{ error: errors.company }"/>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label" for="office-address">{{ localize("오피스 주소") }}
            <span class="required">*</span>
          </label>
          <div class="mb-4">
            <label for="address1" class="detail mb-1">{{ localize('주소') }}
              <span class="required">*</span>
            </label>
            <div class="input-group mt-1">
              <input
                v-model="form.address1"
                type="text"
                id="address1"
                class="form-control curved"
                :placeholder="localize('주소 입력')"
                :class="{ error: errors.address1 }"
              />
            </div>
            <div class="input-group mt-2">
              <input
                v-model="form.address2"
                type="text"
                id="address2"
                class="form-control curved"
                :placeholder="localize('상세 주소 입력')"
                :class="{ error: errors.address2 }"
              />
            </div>
          </div>
          <div class="mb-4">
            <label for="city" class="detail mb-1">{{ localize('도시') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.city"
                type="text"
                id="city"
                class="form-control curved"
                :placeholder="localize('도시')"
                :class="{ error: errors.city }"
              />
            </div>
          </div>
          <div class="mb-4">
            <label for="state-region" class="detail mb-1">{{ localize('주 / 지역') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.state"
                type="text"
                id="state-region"
                class="form-control curved"
                :placeholder="localize('주 / 지역')"
                :class="{ error: errors.state }"
              />
            </div>
          </div>
          <div>
            <label for="postalCode" class="detail mb-1">{{ localize('우편번호') }}
              <span class="required">*</span>
            </label>
            <div class="input-group">
              <input
                v-model="form.postalCode"
                type="text"
                id="postalCode"
                class="form-control curved"
                :placeholder="localize('우편번호')"
                :class="{ error: errors.postalCode }"
              />
            </div>
          </div>
        </div>
      </span>
      <!-- //해외 전용-->
    </form>
    <div class="btn-wrap">
      <a @click="done" class="btn btn-primary center w-100">{{
        localize("완료하기")
      }}</a>
    </div>
  </div>
  <!--  //주소 검색 팝업창-->
</template>

<style scoped lang="scss">
.form-label {
  .required {
    color: red;
    margin-left: 3px;
  }
}
.form-control.error {
  border: 1px solid #ef4444 !important;
}
</style>
